/**
 * compactDdlBodyArm — objectKind → compact body dispatch for the Visual DDL
 * editor hosted inside the Compact VQE popover. Kept apart from
 * `CompactDdlEditor.tsx` so the editor stays a thin shell (fallback + footer)
 * while the per-kind arms accumulate here.
 *
 * Contract (T4 depends on it verbatim): every arm receives
 * `{ editContext, ddlKind, onEditAsSql }` and the module exports one
 * dispatcher returning the arm's body, or `null` when the kind has no
 * embedded body yet — `CompactDdlEditor` then falls back to the read-only
 * `CompactDdlCard`, so unfinished kinds degrade gracefully instead of
 * blanking the popover.
 *
 * Shipped arms:
 *  - `table` → the compact `TableEditor` (the same editor the Table Designer
 *    workspace renders, minus the sidebar).
 *  - `drop`  → `CompactDropArm` (facts + `Check dependents`).
 *  - every other kind → `compactDdlNonTableArms`, which mounts the
 *    `InlineDdl<Kind>Body` export T4a/T4b lifted out of each inline popover.
 *  - `index` → still `null` (fallback card): index add/edit for the table
 *    family lives in the compact TableEditor's Indexes tab and a bare
 *    CREATE INDEX has no dedicated inline form to embed.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3/T4).
 */
import type { ReactNode } from 'react';

import type { DdlStructure } from '../../../sql-resolver/types/ddlEdit';
import type { DdlObjectKind, VqeDdlEditContext } from '../edit/editContext';
import { TableEditor } from '../render/table/TableEditor';
import { CompactDropArm } from './CompactDropArm';
import { renderCompactDdlNonTableArm } from './compactDdlNonTableArms';
import { structureFromDdlEditContext } from './structureFromDdlEditContext';

/**
 * Shared testid prefix for the embedded compact editor. Every per-facet
 * testid the E2E suite drives (`compact-ddl-editor-tab-columns`,
 * `compact-ddl-editor-constraints-add-pk-*`, `compact-ddl-editor-lifecycle-*`,
 * …) is derived from it, so it must stay byte-identical.
 */
export const COMPACT_DDL_EDITOR_TESTID = 'compact-ddl-editor';

export type CompactDdlArmProps = {
  readonly editContext: VqeDdlEditContext;
  readonly ddlKind: string | null;
  readonly onEditAsSql: () => void;
  /**
   * OPTIONAL, additive: "the user cancelled" — the host may close now. Only
   * the form arms use it (their [Cancel] button, which the compact popover
   * pins next to Edit as SQL); the table + drop arms have no Cancel. It is
   * deliberately SEPARATE from the bodies' `onDone`, which also fires on a
   * successful commit: a commit must NOT tear the popover down, because it
   * funnels through the app-shell PreviewDialog over this arm's live context.
   */
  readonly onCancel?: () => void;
  /**
   * OPTIONAL, additive: the caller's already-resolved (and re-parse-sticky)
   * `DdlStructure` for the parked statement — see `useStickyDdlStructure`.
   * Only the `table` arm reads it; omit it and the arm extracts the structure
   * itself, which is correct but drops the body during the resolver's async
   * re-parse window. The three fields above are the frozen arm contract.
   */
  readonly structure?: DdlStructure | null;
};

export function renderCompactDdlBodyArm(
  objectKind: DdlObjectKind,
  props: CompactDdlArmProps,
): ReactNode | null {
  switch (objectKind) {
    case 'table': {
      // No extractable structure → fall back to the read-only card, which at
      // least names the object and its facts (an empty TableEditor would
      // claim the table has no columns). Callers pass the sticky structure so
      // this only happens for a genuinely unsupported head, NOT during the
      // resolver's async re-parse window — see `useStickyDdlStructure`.
      const structure = props.structure ?? structureFromDdlEditContext(props.editContext);
      if (!structure) return null;
      return (
        <TableEditor
          structure={structure}
          variant="compact"
          testId={COMPACT_DDL_EDITOR_TESTID}
          editContext={props.editContext}
          onEditAsSql={props.onEditAsSql}
        />
      );
    }
    case 'drop':
      return <CompactDropArm editContext={props.editContext} />;
    default:
      // view / materialized-view / sequence / type / domain / schema /
      // trigger / routine / package / security (ddlKind-branched) / database.
      // `index` resolves to `null` there and falls back to the read-only card.
      return renderCompactDdlNonTableArm(objectKind, props);
  }
}
